import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { PageSection } from '@openx/components-core/lib/PageSection/PageSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import type { SynchronizedWith } from '@openx/types/deal.ts';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { SyncStatusField } from '../components/SyncStatusField.tsx';

type TddSyncStatusesSectionProps = {
  pdpSync: NonNullable<SynchronizedWith['pdp_sync']>;
};

export const TddSyncStatusesSection = ({ pdpSync }: TddSyncStatusesSectionProps) => {
  const { t } = useTranslation();
  const { usePdpSyncDealStatusOptionsFetch, usePdpSyncNegotiationStatusOptionsFetch, usePdpSyncStatusOptionsFetch } =
    useDealContext();

  const { loading: dealStatusLoading, mapById: dealStatusMapById } = usePdpSyncDealStatusOptionsFetch();
  const { loading: negotiationStatusLoading, mapById: negotiationStatusMapById } =
    usePdpSyncNegotiationStatusOptionsFetch();
  const { loading: syncStatusLoading, mapById: syncStatusMapById } = usePdpSyncStatusOptionsFetch();

  const { pdp_sync_status, pdp_deal_negotiation_status, pdp_deal_status } = pdpSync;
  const pdpLastSyncError = pdpSync.pdp_last_sync_error;

  const syncStatus = pdp_sync_status ? (syncStatusMapById[pdp_sync_status]?.name ?? pdp_sync_status) : pdp_sync_status;
  const negotiationStatus = pdp_deal_negotiation_status
    ? (negotiationStatusMapById[pdp_deal_negotiation_status]?.name ?? pdp_deal_negotiation_status)
    : pdp_deal_negotiation_status;
  const dealStatus = pdp_deal_status ? (dealStatusMapById[pdp_deal_status]?.name ?? pdp_deal_status) : pdp_deal_status;

  const syncStatusTooltip = pdp_sync_status
    ? (syncStatusMapById[pdp_sync_status]?.description ?? undefined)
    : undefined;
  const negotiationStatusTooltip = pdp_deal_negotiation_status
    ? (negotiationStatusMapById[pdp_deal_negotiation_status]?.description ?? undefined)
    : undefined;
  const dealStatusTooltip = pdp_deal_status
    ? (dealStatusMapById[pdp_deal_status]?.description ?? undefined)
    : undefined;

  return (
    <PageSection title={t('Statuses')} dataTest="statuses-section" divider>
      <Grid container spacing={3} size={RIGHT_COLUMN_WIDTH + 1}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SyncStatusField
            label={t('Synchronization Status')}
            loading={syncStatusLoading}
            status={pdp_sync_status}
            statusLabel={syncStatus}
            tooltip={syncStatusTooltip}
            lastErrorDatetime={pdpLastSyncError?.error_datetime}
          />
        </Box>

        <SyncStatusField
          label={t('Negotiation Status')}
          loading={negotiationStatusLoading}
          status={pdp_deal_negotiation_status}
          statusLabel={negotiationStatus}
          tooltip={negotiationStatusTooltip}
        />
        <SyncStatusField
          label={t('Deal Status')}
          loading={dealStatusLoading}
          status={pdp_deal_status}
          statusLabel={dealStatus}
          tooltip={dealStatusTooltip}
        />
      </Grid>
    </PageSection>
  );
};
